import cloneDeep from 'lodash/cloneDeep';
import type { AspectRatio, AudioSourceData } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

import type {
    AspectRatioEnum,
    AudioAssetModel,
    GenerateRecommendedVideoResponse,
    SlideBackgroundModel,
    VideoPlan,
} from 'js/src/client';
import { ApiService, NarrativeThreadEnum, TaskResultMessageTypeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    avatarBackgroundVideoAsset,
    avatarVideoAsset,
    fetchAIAvatarVoicePresets,
    fetchAIVoices,
    fetchAvatars,
    getAIAvatarById,
    getAIAvatarIds,
    getAIAvatarVoicePresetObjects,
    getAIVoices,
} from 'js/src/data/ai-voices-and-avatars';
import { VIDEO_INIT_TYPE } from 'js/src/data/app';
import { fetchBlueprints } from 'js/src/data/blueprints';
import type { ImmutableAudioAsset } from 'js/src/data/media';
import { selectMusicRecommendationMode } from 'js/src/data/media-recommendation-preferences';
import { getCurrentAIAvatarVoicePreset } from 'js/src/data/script-composer/script-composer-selectors';
import {
    getTemplateBundle,
    getTemplateDefaultSceneDesign,
    loadTemplateAndSceneDesigns,
} from 'js/src/data/templates';
import {
    clearLuminaryThumbnail,
    saveLuminaryPreviewForVideoPlanOperation,
} from 'js/src/data/thumbnail-previews';
import { getTranscriptsToSave } from 'js/src/data/transcript';
import {
    editVideoPlan,
    pushVideoPlans,
    setLuminaryInstance,
    setVideoPlanLoadingError,
    setVideoPlanMusicAsset,
    setVideoPlanSceneRecommendations,
    setVideoPlanTemplateStylingVideoData,
    setVideoPlanVoiceoverAsset,
    setVideoPlanVoiceoverLoading,
} from 'js/src/data/video-planner/video-planner-actions';
import { getVideoPlanById } from 'js/src/data/video-planner/video-planner-selectors';
import type { VideoPlanWithCreationInfo } from 'js/src/data/video-planner/video-planner-types';
import { createRecommendationAIVoiceover } from 'js/src/data/video-planner/video-planner-utils';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import { TaskQueue } from 'js/src/libs/task-queue';
import { parseScript } from 'js/src/libs/transcript';
import uuid from 'js/src/libs/uuid';
import { DEFAULT_LUMINARY_CONFIG } from 'js/src/libs/video-data-consts';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    addSceneGroupFromVoiceoverAssetOperation,
    addScenesFromRecommendations,
    applyTemplateStyleFromVideoData,
    createBestGuessTranscript,
    fetchAndSetRecommendedSceneAttributesOperation,
    getNarrativeThreads,
    setCapabilitiesOperation,
} from 'js/src/luminary';
import { createLuminaryOperation, makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

export const getOrCreateLuminaryForVideoPlan = (
    planId: string,
    getState: GetRootState,
    dispatch: AppDispatch,
): Luminary => {
    const plan = getVideoPlanById(getState(), planId);

    if (!plan) {
        throw new Error(`Video plan ${planId} not found`);
    }

    if (plan.luminaryInstance) {
        return plan.luminaryInstance;
    }

    const resolutionValue = window.screen && window.screen.availWidth <= 900 ? 300 : 500;

    const luminary = new Luminary({
        ...DEFAULT_LUMINARY_CONFIG,
        resolutionValue,
    });

    dispatch(
        setLuminaryInstance({
            id: planId,
            luminaryInstance: luminary,
        }),
    );

    return luminary;
};

/**
 * This is the main entry point. Use it to set all video plans in state.
 *
 * We will start fetching the videos for the video plans immediately. As they load the redux state
 * will update with the previews and other information.
 */
export function pushVideoPlansAndFetchVideos({
    videoPlans,
    referenceMaterial,
    autoGenerateVoiceovers = false,
    sourceMediaUUIDs,
}: {
    videoPlans: VideoPlan[];
    referenceMaterial: string;
    autoGenerateVoiceovers?: boolean;
    sourceMediaUUIDs?: string[];
}) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (!videoPlans || videoPlans.length === 0) {
            throw new Error('No video plans provided');
        }

        dispatch(pushVideoPlans({ videoPlans, referenceMaterial }));
        await dispatch(
            fetchVideosForVideoPlans(videoPlans, {
                autoGenerateVoiceovers,
                sourceMediaUUIDs,
            }),
        );
    };
}

/**
 * By default we don't generate AI voiceovers for video plans because that's slow and costly. So
 * when a user shows interest in a video plan (ie. clicks to preview), activate this operation
 * which will generate the AI voiceover for the video plan.
 * If it already is loading, this will do nothing.
 */
export function generateVoiceoverForVideoPlan(id: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const plan = getVideoPlanById(getState(), id);
        if (!plan) {
            throw new Error('Missing video plan');
        }
        if (plan.voiceover.asset) {
            // already has a voiceover, no need to generate again
            return;
        }
        if (plan.voiceover.loading) {
            // already loading, wait until it finishes and then return
            return new Promise<void>((resolve) => {
                const timer = setInterval(() => {
                    const updatedPlan = getVideoPlanById(getState(), id);
                    if (updatedPlan && !updatedPlan.voiceover.loading) {
                        clearInterval(timer);
                        resolve();
                    }
                }, 500);
            });
        }

        if (
            plan.narrative_thread !== NarrativeThreadEnum.VOICEOVER &&
            plan.narrative_thread !== NarrativeThreadEnum.TALKING_HEAD
        ) {
            // no need to generate voiceover for non-voice driven plans
            throw new Error('Video plan is not voice driven, no need to generate voiceover');
        }

        dispatch(setVideoPlanVoiceoverLoading({ id, loading: true }));

        if (!plan.scene_recommendations) {
            throw new Error('no scene recommendations were found');
        }
        const hasFullyLoadedRecommendations = plan.scene_recommendations.every((s) => s !== null);
        if (!hasFullyLoadedRecommendations) {
            throw new Error(
                'Scene recommendations are not fully loaded, cannot generate voiceover',
            );
        }
        if (!plan.ai_voice) {
            throw new Error('No AI voice selected for video plan');
        }

        const { voiceoverAsset, transcript } = await createRecommendationAIVoiceover({
            sceneRecommendations: plan.scene_recommendations,
            videoPlan: plan,
        });
        dispatch(
            setVideoPlanVoiceoverAsset({
                id: plan.id,
                voiceoverAsset: voiceoverAsset.toJS(),
                transcript,
            }),
        );
    };
}

export const loadVideoPlanOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        videoPlan: VideoPlanWithCreationInfo,
        isGlobalVideo: boolean,
    ) => {
        const defaultSceneDesign = getTemplateDefaultSceneDesign(getState(), videoPlan.template_id);
        if (!defaultSceneDesign) {
            throw new Error('Default scene design not found');
        }
        const templateBundle = getTemplateBundle(getState(), videoPlan.template_id);
        if (!templateBundle) {
            throw new Error('Template bundle not found');
        }

        // reset luminary state
        await luminary.clear();

        // we don't want to show any loading scenes, so strip to only the scenes that are ready
        const scene_recommendations = videoPlan.scene_recommendations!.filter(
            (recommendation) => recommendation !== null,
        );

        luminary.setTemplate(templateBundle.toJS());
        call(setCapabilitiesOperation);
        if (videoPlan.template_styling_video_data) {
            await applyTemplateStyleFromVideoData(
                luminary,
                cloneDeep(videoPlan.template_styling_video_data),
            );
        }

        luminary.aspectRatio = videoPlan.aspect_ratio as unknown as AspectRatio;
        if (videoPlan.narrative_thread === NarrativeThreadEnum.TEXT) {
            addScenesFromRecommendations(
                scene_recommendations,
                luminary,
                dispatch,
                {},
                defaultSceneDesign,
                'video_plan',
                call,
                false, // not optimizing
                isGlobalVideo,
                true,
                '',
            );
        } else if (
            videoPlan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD ||
            videoPlan.narrative_thread === NarrativeThreadEnum.VOICEOVER
        ) {
            let avatarAsset: ImmutableMap<SlideBackgroundModel> | null = null;
            if (videoPlan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD) {
                if (!videoPlan.ai_avatar) {
                    throw new Error('No AI avatar selected for talking head video plan');
                }
                const avatar = getAIAvatarById(getState(), videoPlan.ai_avatar);
                if (!avatar) {
                    throw new Error('AI avatar not found');
                }
                avatarAsset = avatarVideoAsset(avatar);
                if (!avatarAsset) throw new Error('Avatar asset not found');
            }
            const avatarBackgroundAssetData = avatarAsset
                ? avatarBackgroundVideoAsset(getCurrentAIAvatarVoicePreset(getState()))
                : null;
            if (!videoPlan.ai_voice) {
                throw new Error('No AI voice selected for voice-driven video plan');
            }

            if (!scene_recommendations) throw new Error('Scene recommendations not found');
            const loadedSceneRecommendations = scene_recommendations.filter(
                (recommendation) => recommendation !== null,
            );

            let audioAsset: ImmutableAudioAsset;
            let transcript = videoPlan.voiceover.transcript || null;
            if (isGlobalVideo || videoPlan.voiceover.asset) {
                if (!videoPlan.voiceover.asset)
                    throw new Error('Video plan voiceover asset data not found');
                audioAsset = ImmutableFromJS(videoPlan.voiceover.asset);
            } else {
                const bestGuessTranscript = createBestGuessTranscript(scene_recommendations);
                audioAsset = bestGuessTranscript.audioAsset;
                transcript = bestGuessTranscript.transcript;
            }

            await call(addSceneGroupFromVoiceoverAssetOperation, {
                targetIndex: 0,
                audioAssetData: audioAsset,
                avatarAssetData: avatarAsset,
                avatarBackgroundAssetData,
                deleteOtherScenes: true,
                analyticsMetadata: { method: 'AI Voice Script Composer Blueprint' },
                selectedBlueprintId: videoPlan.blueprint_id,
                generateRecommendedVideoResponse: null,
                sceneRecommendations: loadedSceneRecommendations,
                isGlobalVideo,
                transcript,
                systemTriggered: true,
                parentActionGroup: uuid(),
            });
        } else {
            throw new Error('Invalid narrative thread');
        }

        if (videoPlan.music_asset) {
            try {
                luminary.music = videoPlan.music_asset as AudioSourceData;
            } catch (error) {
                ErrorReporter.capture(error, {
                    extra: { videoPlan, musicAsset: videoPlan.music_asset },
                });
            }
        }
    },
);

export const saveVideoPlanVideoToDatabaseOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            videoPlanId,
            aspectRatio,
            isSpinoff,
        }: {
            videoPlanId: string;
            aspectRatio: AspectRatio;
            isSpinoff?: boolean;
        },
    ): Promise<number> => {
        const videoPlan = getVideoPlanById(getState(), videoPlanId);
        if (!videoPlan) {
            throw new Error('Missing video plan');
        }
        const hasFullyLoadedRecommendations =
            videoPlan.scene_recommendations !== null &&
            videoPlan.scene_recommendations.every((scene) => scene !== null);
        if (!hasFullyLoadedRecommendations) {
            throw new Error('Scene recommendations are not fully loaded, cannot save video');
        }
        const isVoiceDriven =
            videoPlan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD ||
            videoPlan.narrative_thread === NarrativeThreadEnum.VOICEOVER;
        const hasVoiceoverAsset = videoPlan.voiceover.asset !== null;
        if (isVoiceDriven && !hasVoiceoverAsset) {
            Toast.loading('Generating voiceover, please wait...');
            await dispatch(generateVoiceoverForVideoPlan(videoPlanId));
            await call(loadVideoPlanOperation, videoPlan, false);
        }
        luminary.aspectRatio = aspectRatio;

        const response = await ApiService.videoCreate({
            body: {
                video_data: luminary.save(),
                aspect_ratio: luminary.aspectRatio,
                template: luminary.template.id,
                script_composer_data: null,
                created_from_spinoff: isSpinoff,
            },
        });
        const video_id = response.data.video_id;
        // currently the create endpoint doesn't support all necessary fields, so need to do an update
        // TODO: would be better to update the create endpoint to accept these fields
        await ApiService.videoUpdate({
            path: { video_id: video_id.toString() },
            body: {
                video_data: luminary.save(),
                script: parseScript(luminary),
                blueprint_id: videoPlan.blueprint_id,
                transcripts: getTranscriptsToSave(getState()),
                narrative_threads: getNarrativeThreads(luminary),
                duration_seconds: Math.floor(luminary.duration / 1000),
                user_overrides: {},
                user_uploads: [],
                brand_kit_id: null,
                template_id: luminary.template.id,
                aspect_ratio: luminary.aspectRatio as unknown as AspectRatioEnum,
                video_data_hash: response.data.video_data_hash,
                video_updated_by_user: false,
                video_init_type: VIDEO_INIT_TYPE.CREATE_FROM_SCRATCH,
                script_composer_data: null,
                article_original_content: null,
                tab_durations: null,
                render: false,
                async_generation_id: null,
                async_generation_data: null,
            },
        });
        await ApiService.videoPartialUpdate({
            path: { video_id: video_id.toString() },
            body: {
                name: videoPlan.title,
            },
        });
        Toast.success('Video saved successfully!');
        return video_id;
    },
);

export function _fetchAIVoicesAndAvatars() {
    /**
     * We need to load all ai voices, avatars, and voice presets before we can start generating
     * videos based on voice-driven video plans.
     * We do this in parallel to speed up the loading process.
     */
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const avatarPromise = dispatch(fetchAvatars());
        const aiVoicePromise = dispatch(fetchAIVoices());
        const aiAvatarVoicePresetsPromise = dispatch(fetchAIAvatarVoicePresets());

        // if any of the promises fail, throw an error
        const [avatars, aiVoices, aiAvatarVoicePresets] = await Promise.all([
            avatarPromise,
            aiVoicePromise,
            aiAvatarVoicePresetsPromise,
        ]);
        if (!avatars || !aiVoices || !aiAvatarVoicePresets) {
            throw new Error('Failed to fetch content for video plan fetching');
        }
    };
}

export function _fetchTemplateStylingVideoDataForVideoPlan(id: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            const plan = getVideoPlanById(getState(), id);
            if (!plan) throw new Error('Missing video plan');
            if (!plan.blueprint_id) {
                return; // no blueprint, no template styling video data
            }

            const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
                path: {
                    id: plan.blueprint_id,
                },
                query: {
                    template: plan.template_id,
                },
            });
            dispatch(
                setVideoPlanTemplateStylingVideoData({
                    id,
                    templateStylingVideoData: JSON.parse(response.data.video_data),
                }),
            );
        } catch (error) {
            ErrorReporter.capture(error);
        }
    };
}

export function _fetchSceneAttributesForVideoPlan(id: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            const plan = getVideoPlanById(getState(), id);
            if (!plan) throw new Error('Missing video plan');

            // load the video in data luminary instance
            const dataLuminary = new Luminary({
                fetchResources: false,
                disableRenderer: true,
                autoDraw: false,
                autoUpdate: false,
            });

            const { loadVideoPlan, fetchAndSetRecommendedSceneAttributes } = makeOperationsCallable(
                {
                    loadVideoPlanOperation,
                    fetchAndSetRecommendedSceneAttributesOperation,
                },
                dataLuminary,
                dispatch,
                getState,
            );

            await loadVideoPlan(plan, false);

            // get the scene attributes for each scene (in parallel)
            const promises = dataLuminary.scenes.map((scene) => {
                return fetchAndSetRecommendedSceneAttributes({
                    sceneId: scene.id,
                    isGlobalVideo: false,
                });
            });
            await Promise.all(promises);

            // cleanup
            await dataLuminary.destroy();
        } catch (error) {
            ErrorReporter.capture(error);
        }
    };
}

export function _fetchMusicRecommendationForVideoPlan(id: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            const plan = getVideoPlanById(getState(), id);
            if (!plan) throw new Error('Missing video plan');
            if (plan.scene_recommendations === null)
                throw new Error('Scene recommendations not available');

            // get the script
            const script = plan.scene_recommendations
                .filter((recc) => recc)
                .map((recc) => recc.scene_data.segment)
                .join(' ');
            const minDuration = 2 * plan.scene_recommendations.length; // estimate
            const musicRecommendationMode = selectMusicRecommendationMode(getState());

            const taskResponse = await ApiService.musicRecommendationCreate({
                body: {
                    text: script,
                    min_duration: minDuration,
                    music_recommendation_mode: musicRecommendationMode,
                },
            });
            const ws = WebSocketClient.getInstance();
            const musicAsset = await ws.waitForMessage<AudioAssetModel>(
                taskResponse.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'music_recommendation',
            );
            dispatch(setVideoPlanMusicAsset({ id, musicAsset }));
        } catch (error) {
            ErrorReporter.capture(error);
        }
    };
}

function _fetchAncillaryDataNecessaryToRenderVideoPlans(plans: VideoPlan[]) {
    /**
     * This method kicks off requests to load all necessary data to render a video plan, including:
     * - blueprint
     * - template styling video data
     * - template data
     */
    return async (dispatch: AppDispatch) => {
        // load video plan template styling video data for all plans
        // don't wait for them here – wait later on
        const stylingPromises = plans.map((plan) =>
            dispatch(_fetchTemplateStylingVideoDataForVideoPlan(plan.id)),
        );

        // also kick off loading the template data for each template (will wait later on)
        const templatesDeduped = [...new Set(plans.map((plan) => plan.template_id))];
        const templatePromises = templatesDeduped.map((templateId) =>
            dispatch(loadTemplateAndSceneDesigns(templateId)),
        );

        // load blueprints
        const blueprint_ids = plans.map((plan) => plan.blueprint_id).filter((id) => id !== null);
        const { success: blueprintFetchSuccess, blueprints } = await dispatch(
            fetchBlueprints({ ids: blueprint_ids }),
        );
        if (!blueprintFetchSuccess) {
            return;
        }
        if (blueprints.length === 0) {
            return;
        }

        // now wait for the styling/template promises to finish
        await Promise.all(stylingPromises);
        await Promise.all(templatePromises);
    };
}

function _ensureVoicesAndAvatarsAreValid(plans: VideoPlan[]) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        // check that we've loaded AI voices/avatars if any voicedriven plans
        const hasVoiceDrivenPlans = plans.some(
            (plan) =>
                plan.narrative_thread === NarrativeThreadEnum.VOICEOVER ||
                plan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD,
        );
        if (hasVoiceDrivenPlans) {
            let aiVoices = getAIVoices(getState());
            let aiAvatars = getAIAvatarIds(getState());
            let aiAvatarPresets = getAIAvatarVoicePresetObjects(getState());
            const notLoaded =
                !aiVoices ||
                aiVoices.size === 0 ||
                !aiAvatars ||
                aiAvatars.size === 0 ||
                !aiAvatarPresets ||
                aiAvatarPresets.size === 0;
            if (notLoaded) {
                // eslint-disable-next-line no-console
                console.warn(
                    'AI voices or avatars not loaded, fetching them now – should update to prefetch!',
                );
                await dispatch(_fetchAIVoicesAndAvatars());
                aiVoices = getAIVoices(getState());
                aiAvatars = getAIAvatarIds(getState());
                aiAvatarPresets = getAIAvatarVoicePresetObjects(getState());
            }

            // check that all avatars/voices are valid
            for (const plan of plans) {
                if (plan.narrative_thread === NarrativeThreadEnum.VOICEOVER) {
                    // voiceover plans should not have an avatar
                    if (plan.ai_avatar) {
                        plan.ai_avatar = null;
                        dispatch(editVideoPlan({ videoPlan: plan }));
                    }
                    const matchingVoice = aiVoices.find((voice) => voice.slug === plan.ai_voice);
                    if (!matchingVoice) {
                        // if the voice doesn't match, set it to the first voice
                        const firstVoice = aiVoices.first();
                        if (!firstVoice) {
                            throw new Error('No AI voices found, cannot generate videos');
                        }
                        plan.ai_voice = firstVoice.slug;
                        dispatch(editVideoPlan({ videoPlan: plan }));
                    }
                } else if (plan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD) {
                    // talking head plans should have an avatar
                    const matchingPreset = aiAvatarPresets.find(
                        (preset) => preset.avatar_id === plan.ai_avatar,
                    );
                    if (matchingPreset) {
                        // ensure the voice matches
                        if (matchingPreset.voice_slug !== plan.ai_voice) {
                            // if the voice doesn't match, set it to voice in the preset
                            plan.ai_voice = matchingPreset.voice_slug;
                            dispatch(editVideoPlan({ videoPlan: plan }));
                        }
                    } else {
                        // update the avatar to match the first preset
                        const firstPreset = aiAvatarPresets.first();
                        if (!firstPreset) {
                            throw new Error('No AI avatar presets found, cannot generate videos');
                        }
                        plan.ai_avatar = firstPreset.avatar_id;
                        plan.ai_voice = firstPreset.voice_slug;
                        dispatch(editVideoPlan({ videoPlan: plan }));
                    }
                } else if (plan.narrative_thread === NarrativeThreadEnum.TEXT) {
                    // text plans should not have an avatar or voice
                    if (plan.ai_avatar) {
                        plan.ai_avatar = null;
                        dispatch(editVideoPlan({ videoPlan: plan }));
                    }
                    if (plan.ai_voice) {
                        plan.ai_voice = null;
                        dispatch(editVideoPlan({ videoPlan: plan }));
                    }
                }
            }
        }
        return plans;
    };
}

export function fetchVideosForVideoPlans(
    videoPlans: VideoPlan[],
    {
        autoGenerateVoiceovers = false,
        loadMusicRecommendation = true,
        generateThumbnail = true,
        sourceMediaUUIDs,
    }: {
        autoGenerateVoiceovers?: boolean;
        loadMusicRecommendation?: boolean;
        generateThumbnail?: boolean;
        sourceMediaUUIDs?: string[];
    },
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            videoPlans = await dispatch(_ensureVoicesAndAvatarsAreValid(videoPlans));

            // Get reference material from the first video plan in Redux state
            const state = getState();
            const videoPlanWithCreationInfo = state.videoPlanner.videoPlans.find((plan) =>
                videoPlans.some((inputPlan) => inputPlan.id === plan.id),
            );
            const referenceMaterial = videoPlanWithCreationInfo?.reference_material || '';
            if (referenceMaterial === '') {
                throw new Error('No reference material provided for video plans');
            }

            // clear preview thumbnail if exists
            for (const plan of videoPlans) {
                dispatch(clearLuminaryThumbnail({ id: plan.id }));
            }

            // kick off data fetch
            const ancillaryDataPromise = dispatch(
                _fetchAncillaryDataNecessaryToRenderVideoPlans(videoPlans),
            );

            // kick off video generation for each video plan
            const response = await ApiService.generateVideosForVideoPlansCreate({
                body: {
                    reference_material: referenceMaterial,
                    video_plans: videoPlans.map((plan) => {
                        return {
                            title: plan.title,
                            content_prompt: plan.content_prompt,
                            video_prompt: plan.video_prompt,
                            script: plan.script,
                            duration_target_seconds: plan.duration_target_seconds,
                            template_id: plan.template_id,
                            narrative_thread: plan.narrative_thread,
                            blueprint_id: plan.blueprint_id,
                            aspect_ratio: plan.aspect_ratio,
                            ai_voice: plan.ai_voice,
                        };
                    }),
                    source_media_uuids: sourceMediaUUIDs,
                },
            });

            // wait for data to load (need to before we start processing video generations)
            await ancillaryDataPromise;

            // to avoid generating too many luminary instances & using up all WebGL contexts, use a task queue
            // for calling saveLuminaryPreviewForVideoPlanOperation
            const previewTaskQueue = new TaskQueue(2, 0);

            const taskPromises = response.data.task_ids.map(
                async (taskId: string, index: number) => {
                    const id = videoPlans[index].id;
                    let hasLoadedFirstScene = false;
                    try {
                        const ws = WebSocketClient.getInstance();
                        const recommendedVideo =
                            await ws.waitForMessage<GenerateRecommendedVideoResponse>(
                                taskId,
                                TaskResultMessageTypeEnum.TASK_RESULT,
                                'generate_recommended_video',
                                {
                                    partialResponseCallback: (partialResult) => {
                                        dispatch(
                                            setVideoPlanSceneRecommendations({
                                                id,
                                                sceneRecommendations: partialResult.scenes,
                                            }),
                                        );
                                        // if we just loaded the first scene, generate a thumbnail image
                                        if (generateThumbnail) {
                                            if (hasLoadedFirstScene) {
                                                return;
                                            }
                                            if (
                                                !partialResult.scenes ||
                                                partialResult.scenes.length === 0
                                            ) {
                                                return;
                                            }

                                            hasLoadedFirstScene = true;
                                            previewTaskQueue.queueTask(async () => {
                                                await dispatch(
                                                    saveLuminaryPreviewForVideoPlanOperation(id),
                                                );
                                            });
                                        }
                                    },
                                },
                            );

                        dispatch(
                            setVideoPlanSceneRecommendations({
                                id,
                                sceneRecommendations: recommendedVideo.scenes,
                            }),
                        );

                        if (autoGenerateVoiceovers) {
                            const containsVoiceover =
                                videoPlans[index].narrative_thread ===
                                    NarrativeThreadEnum.VOICEOVER ||
                                videoPlans[index].narrative_thread ===
                                    NarrativeThreadEnum.TALKING_HEAD;
                            if (containsVoiceover) {
                                await dispatch(generateVoiceoverForVideoPlan(id));
                            }
                        }

                        // load scene attribute recommendations
                        const attrRecProm = dispatch(_fetchSceneAttributesForVideoPlan(id));

                        // if we still don't have a thumbnail, generate one
                        let thumbProm = Promise.resolve();
                        if (generateThumbnail) {
                            if (!hasLoadedFirstScene) {
                                thumbProm = previewTaskQueue.queueTask(async () => {
                                    await dispatch(saveLuminaryPreviewForVideoPlanOperation(id));
                                });
                            }
                        }

                        // load music recommendation
                        let musicProm = Promise.resolve();
                        if (loadMusicRecommendation) {
                            musicProm = dispatch(_fetchMusicRecommendationForVideoPlan(id));
                        }

                        await Promise.all([thumbProm, musicProm, attrRecProm]);
                    } catch (error) {
                        ErrorReporter.capture(error);
                        dispatch(setVideoPlanLoadingError({ id }));
                        return false;
                    }
                    return true;
                },
            );

            const results = await Promise.all(taskPromises);

            // return true if ANY of the tasks were successful
            return results.some((result) => result);
        } catch (error) {
            ErrorReporter.capture(error);
            return false;
        }
    };
}

const SKETCH_LEAVE_MESSAGE =
    'Are you sure you want to quit? Any changes since your last save will not be saved.';

let sketchBeforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

/**
 * Enable or disable the browser leave warning for Sketch.
 * In DEBUG mode, shows a toast instead of a browser prompt.
 */
export function updateSketchLeaveWarning(shouldShow: boolean) {
    // Remove existing handler if any
    if (sketchBeforeUnloadHandler) {
        window.removeEventListener('beforeunload', sketchBeforeUnloadHandler);
        sketchBeforeUnloadHandler = null;
    }

    if (shouldShow) {
        sketchBeforeUnloadHandler = (event: BeforeUnloadEvent) => {
            if (SERVER.DEBUG) {
                Toast.info('Disabling window.onbeforeunload warning in DEBUG mode', 'FYI!');
                return null;
            }
            event.preventDefault();
            return SKETCH_LEAVE_MESSAGE;
        };
        window.addEventListener('beforeunload', sketchBeforeUnloadHandler);
    }
}
